import { IBusinessEntity } from '@commenda/types';
import { ObligationRulesCategory } from '@prisma/client';
import { useCallback, useEffect, useState } from 'react';
import { RbcEvent } from './calendar.types';

const ALL_CATEGORIES = new Set(Object.values(ObligationRulesCategory));

interface CalendarFilters {
  visibilityMap: Map<number, boolean>;
  enabledCategories: Set<ObligationRulesCategory>;
  toggleEntity: (entityId: number) => void;
  toggleCategory: (category: ObligationRulesCategory) => void;
  selectAllCategories: () => void;
  clearCategories: () => void;
  selectAllEntities: () => void;
  clearEntities: () => void;
  applyFilters: (events: RbcEvent[]) => RbcEvent[];
}

export const useCalendarFilters = (
  entities: IBusinessEntity[],
  _events: RbcEvent[]
): CalendarFilters => {
  const [visibilityMap, setVisibilityMap] = useState<Map<number, boolean>>(
    () => new Map(entities.map((e) => [e.id, true]))
  );
  const [enabledCategories, setEnabledCategories] = useState<
    Set<ObligationRulesCategory>
  >(ALL_CATEGORIES);

  // Sync new entities into visibilityMap (default visible) without resetting
  // existing toggles. Key on the joined id string so we only re-run when the
  // entity set actually changes, not on every render.
  const entityIds = entities.map((e) => e.id).join(',');
  useEffect(() => {
    setVisibilityMap((prev) => {
      const next = new Map(prev);
      for (const entity of entities) {
        if (!next.has(entity.id)) next.set(entity.id, true);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityIds]);

  const toggleEntity = useCallback((entityId: number) => {
    setVisibilityMap((prev) => {
      const next = new Map(prev);
      next.set(entityId, !(next.get(entityId) ?? true));
      return next;
    });
  }, []);

  const toggleCategory = useCallback((category: ObligationRulesCategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const selectAllCategories = useCallback(() => {
    setEnabledCategories(new Set(ALL_CATEGORIES));
  }, []);

  const clearCategories = useCallback(() => {
    setEnabledCategories(new Set());
  }, []);

  const selectAllEntities = useCallback(() => {
    setVisibilityMap((prev) => {
      const next = new Map(prev);
      next.forEach((_, id) => {
        next.set(id, true);
      });
      return next;
    });
  }, []);

  const clearEntities = useCallback(() => {
    setVisibilityMap((prev) => {
      const next = new Map(prev);
      next.forEach((_, id) => {
        next.set(id, false);
      });
      return next;
    });
  }, []);

  const applyFilters = useCallback(
    (eventsToFilter: RbcEvent[]): RbcEvent[] =>
      eventsToFilter.filter(
        (e) =>
          visibilityMap.get(e.resource.corporationId) !== false &&
          enabledCategories.has(e.resource.category)
      ),
    [visibilityMap, enabledCategories]
  );

  return {
    visibilityMap,
    enabledCategories,
    toggleEntity,
    toggleCategory,
    selectAllCategories,
    clearCategories,
    selectAllEntities,
    clearEntities,
    applyFilters,
  };
};
